<template>
  <div class="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="modal-title">
    <div class="modal-content">
      <button @click="closeModal" class="close-btn" aria-label="Close modal" type="button">&times;</button>

      <div class="modal-header">
        <h2 id="modal-title">Create Account</h2>
        <p class="modal-subtitle">Join We Search Research to start your search</p>
      </div>

      <form @submit.prevent="handleRegister" class="register-form">

        <Alert v-if="errors.general" variant="destructive" class="register-alert">
          <AlertDescription>{{ errors.general }}</AlertDescription>
        </Alert>

        <!-- OAuth Registration Buttons -->
        <div class="oauth-section">
          <Button
            type="button"
            class="oauth-button oauth-google"
            @click="handleOAuthRegister('google')"
            :disabled="loading"
          >
            <svg class="oauth-icon" viewBox="0 0 24 24" width="20" height="20">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign up with Google
          </Button>

          <!-- <Button
            type="button"
            class="oauth-button oauth-microsoft"
            @click="handleOAuthRegister('microsoft')"
            :disabled="loading"
          >
            <svg class="oauth-icon" viewBox="0 0 24 24" width="20" height="20">
              <path fill="#F25022" d="M1 1h10v10H1z"/>
              <path fill="#7FBA00" d="M13 1h10v10H13z"/>
              <path fill="#00A4EF" d="M1 13h10v10H1z"/>
              <path fill="#FFB900" d="M13 13h10v10H13z"/>
            </svg>
            Sign up with Microsoft
          </Button>

          <Button
            type="button"
            class="oauth-button oauth-facebook"
            @click="handleOAuthRegister('facebook')"
            :disabled="loading"
          >
            <svg class="oauth-icon" viewBox="0 0 24 24" width="20" height="20" fill="#1877F2">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            Sign up with Facebook
          </Button> -->
        </div>

        <div class="divider">
          <span>or</span>
        </div>

        <div class="name-row">
          <div class="form-group">
            <Label for="firstName">First Name</Label>
            <Input
              id="firstName"
              v-model="form.firstName"
              type="text"
              required
              autocomplete="given-name"
              maxlength="100"
              placeholder="First name"
              :class="{ 'input-error': errors.firstName }"
              aria-describedby="firstName-error"
              :aria-invalid="!!errors.firstName"
            />
            <span v-if="errors.firstName" id="firstName-error" class="error-message" role="alert">{{ errors.firstName }}</span>
          </div>

          <div class="form-group">
            <Label for="lastName">Last Name</Label>
            <Input
              id="lastName"
              v-model="form.lastName"
              type="text"
              required
              autocomplete="family-name"
              maxlength="100"
              placeholder="Last name"
              :class="{ 'input-error': errors.lastName }"
              aria-describedby="lastName-error"
              :aria-invalid="!!errors.lastName"
            />
            <span v-if="errors.lastName" id="lastName-error" class="error-message" role="alert">{{ errors.lastName }}</span>
          </div>
        </div>

        <div class="form-group">
          <Label for="email">Email Address</Label>
          <Input
            id="email"
            v-model="form.email"
            type="email"
            required
            placeholder="Enter your email"
            :class="{ 'input-error': errors.email }"
            aria-describedby="email-error"
            :aria-invalid="!!errors.email"
          />
          <span v-if="errors.email" id="email-error" class="error-message" role="alert">{{ errors.email }}</span>
        </div>

        <div class="form-group">
          <Label for="password">Password</Label>
          <Input
            id="password"
            v-model="form.password"
            type="password"
            required
            placeholder="Create a password"
            :class="{ 'input-error': errors.password }"
            aria-describedby="password-error password-hint"
            :aria-invalid="!!errors.password"
          />
          <span id="password-hint" class="hint-message">
            At least 10 characters, with uppercase, lowercase, a digit, and a special character.
          </span>
          <span v-if="errors.password" id="password-error" class="error-message" role="alert">{{ errors.password }}</span>
        </div>

        <div class="form-group">
          <Label for="confirmPassword">Confirm Password</Label>
          <Input
            id="confirmPassword"
            v-model="form.confirmPassword"
            type="password"
            required
            placeholder="Confirm your password"
            :class="{ 'input-error': errors.confirmPassword }"
            aria-describedby="confirmPassword-error"
            :aria-invalid="!!errors.confirmPassword"
          />
          <span v-if="errors.confirmPassword" id="confirmPassword-error" class="error-message" role="alert">{{ errors.confirmPassword }}</span>
        </div>

        <div class="consent-block" :class="{ 'consent-block--error': errors.consent }">
          <label class="consent-row" for="register-consent">
            <input
              id="register-consent"
              v-model="form.consentAccepted"
              type="checkbox"
              class="consent-checkbox"
              :aria-invalid="!!errors.consent"
              aria-describedby="consent-error consent-text"
            />
            <span id="consent-text" class="consent-text">
              <span class="consent-intro">By registering with We Search Research, I agree:</span>
              <ul class="consent-list">
                <li>
                  To the
                  <a
                    :href="CONSENT_LINKS.PRIVACY_POLICY"
                    target="_blank"
                    rel="noopener noreferrer"
                  >Privacy Policy</a>.
                </li>
                <li>
                  To the
                  <a
                    :href="CONSENT_LINKS.TERMS_OF_USE"
                    target="_blank"
                    rel="noopener noreferrer"
                  >Terms of Use</a>.
                </li>
                <li>
                  To allow We Search Research to capture my conversations with the
                  Search Assistant for the purpose of matching me to a clinical trial
                  and connecting me with the research team leading a trial.
                </li>
              </ul>
            </span>
          </label>
          <span
            v-if="errors.consent"
            id="consent-error"
            class="error-message consent-error"
            role="alert"
          >{{ errors.consent }}</span>
        </div>

        <Button
          type="submit"
          class="register-submit-btn"
          :disabled="loading || !form.consentAccepted"
          :aria-busy="loading"
          aria-label="Create a new account"
        >
          <span v-if="loading">Creating Account...</span>
          <span v-else>Create Account</span>
        </Button>

        <div class="form-footer">
          <p>Already have an account?
            <a href="#" @click.prevent="switchToLogin">Sign in here</a>
          </p>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import api from '@/api'
import {
  API_ENDPOINTS,
  HTTP_STATUS,
  VALIDATION,
  ERROR_MESSAGES,
  CONSENT_VERSION,
  CONSENT_LINKS
} from '@/constants'
import { initiateOAuth } from '@/utils/oauth'
import { validateStrongPassword } from '@/utils/passwordPolicy'

export default {
  name: 'RegisterModal',
  components: {
    Button,
    Input,
    Label,
    Alert,
    AlertDescription
  },
  data() {
    return {
      form: {
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        consentAccepted: false
      },
      errors: {},
      loading: false,
      CONSENT_LINKS
    }
  },
  methods: {
    validateForm() {
      this.errors = {}

      const firstName = (this.form.firstName || '').trim()
      if (!firstName) {
        this.errors.firstName = 'First name is required'
      } else if (firstName.length > 100) {
        this.errors.firstName = 'First name must be 100 characters or fewer'
      }

      const lastName = (this.form.lastName || '').trim()
      if (!lastName) {
        this.errors.lastName = 'Last name is required'
      } else if (lastName.length > 100) {
        this.errors.lastName = 'Last name must be 100 characters or fewer'
      }

      if (!this.form.email) {
        this.errors.email = 'Email is required'
      } else if (!VALIDATION.EMAIL_REGEX.test(this.form.email)) {
        this.errors.email = 'Please enter a valid email address'
      }

      const passwordError = validateStrongPassword(this.form.password)
      if (passwordError) {
        this.errors.password = passwordError
      }

      if (!this.form.confirmPassword) {
        this.errors.confirmPassword = 'Please confirm your password'
      } else if (this.form.password !== this.form.confirmPassword) {
        this.errors.confirmPassword = 'Passwords do not match'
      }

      if (!this.form.consentAccepted) {
        this.errors.consent = 'You must agree to the Privacy Policy and Terms of Use to continue.'
      }

      return Object.keys(this.errors).length === 0
    },
    async handleRegister() {
      if (!this.validateForm()) {
        return
      }

      this.loading = true

      try {
        await api.post(API_ENDPOINTS.REGISTER, {
          first_name: this.form.firstName.trim(),
          last_name: this.form.lastName.trim(),
          email: this.form.email,
          password: this.form.password,
          consent_accepted: this.form.consentAccepted,
          consent_version: CONSENT_VERSION
        })

        // Registration succeeded. Do NOT attempt to auto-login here: the
        // backend's /login endpoint is OTP-gated and does not return an
        // access_token on the first call (it emails a code instead). The
        // parent will close this modal and open LoginModal pre-filled with
        // the user's email so they can complete the OTP flow.
        this.$emit('register-success', { email: this.form.email })

      } catch (error) {
        if (error.response?.status === HTTP_STATUS.NOT_FOUND || error.response?.status === 400) {
          this.errors.general = error.response.data.detail || 'Registration failed'
        } else if (error.response?.data?.detail) {
          this.errors.general = error.response.data.detail
        } else {
          this.errors.general = ERROR_MESSAGES.GENERIC_ERROR
        }
      } finally {
        this.loading = false
      }
    },
    closeModal() {
      this.$emit('close')
    },
    switchToLogin() {
      this.$emit('close')
      this.$emit('show-login')
    },
    handleOAuthRegister(provider) {
      try {
        initiateOAuth(provider)
      } catch (error) {
        this.errors.general = 'Failed to initiate OAuth registration. Please try again.'
      }
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background:
    radial-gradient(circle, rgba(255, 255, 255, 0.04) 1px, transparent 1.4px) 0 0 / 14px 14px,
    radial-gradient(ellipse at top left, rgba(255, 255, 255, 0.05), transparent 70%),
    #10372f;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1.5rem;
}

.modal-content {
  position: relative;
  background: rgba(10, 45, 36, 0.55);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 18px;
  width: 100%;
  max-width: 460px;
  max-height: 92vh;
  overflow-y: auto;
  color: #faf9f7;
  padding: 2.5rem 2.25rem;
}

.modal-header {
  text-align: center;
  margin-bottom: 1.75rem;
}

.modal-header h2 {
  margin: 0 0 0.5rem;
  font-size: 1.625rem;
  font-weight: 600;
  color: #faf9f7;
  letter-spacing: 0.2px;
}

.modal-subtitle {
  margin: 0;
  color: rgba(255, 255, 255, 0.68);
  font-size: 0.95rem;
  font-weight: 400;
}

.close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.7);
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background 0.15s ease, color 0.15s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
}

.register-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.name-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

@media (max-width: 480px) {
  .name-row {
    grid-template-columns: 1fr;
  }
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* Labels — visually hidden but accessible */
.form-group :deep(label) {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Inputs — transparent over dark green */
.form-group :deep(input) {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: #ffffff;
  border-radius: 9999px;
  padding: 0.85rem 1.25rem;
  height: auto;
  font-size: 0.95rem;
}

.form-group :deep(input::placeholder) {
  color: rgba(255, 255, 255, 0.55);
}

.form-group :deep(input:focus),
.form-group :deep(input:focus-visible) {
  outline: none;
  border-color: #ffffff;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.15);
}

.input-error {
  border-color: #fca5a5 !important;
}

.error-message {
  color: #fca5a5;
  font-size: 0.8125rem;
  margin-top: 0.125rem;
  display: block;
  padding-left: 1.25rem;
}

/* Consent block — translucent over dark green */
.consent-block {
  margin: 0.25rem 0 0.25rem;
  padding: 0.85rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.04);
}

.consent-block--error {
  border-color: rgba(252, 165, 165, 0.6);
  background: rgba(252, 165, 165, 0.08);
}

.consent-row {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  cursor: pointer;
}

.consent-checkbox {
  margin-top: 0.2rem;
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  accent-color: #faf9f7;
  cursor: pointer;
}

.consent-text {
  font-size: 0.8125rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.85);
}

.consent-intro {
  display: block;
  font-weight: 500;
  margin-bottom: 0.25rem;
  color: #faf9f7;
}

.consent-list {
  margin: 0;
  padding-left: 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  color: rgba(255, 255, 255, 0.78);
}

.consent-list a {
  color: #ffffff;
  font-weight: 600;
  text-decoration: underline;
}

.consent-list a:hover {
  color: #ffffff;
  text-decoration: none;
}

.consent-error {
  margin-top: 0.5rem;
  padding-left: 1.625rem;
}

/* Submit — cream pill */
.register-submit-btn {
  width: 100%;
  background: #faf9f7;
  color: #0a1d14;
  border-radius: 9999px;
  padding: 0.95rem 1.25rem;
  height: auto;
  font-size: 1rem;
  font-weight: 600;
  border: none;
  margin-top: 0.5rem;
}

.register-submit-btn:hover:not(:disabled) {
  background: #ffffff;
  color: #0a1d14;
}

.register-submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.register-alert {
  margin-bottom: 0.5rem;
  background: rgba(252, 165, 165, 0.12);
  border-color: rgba(252, 165, 165, 0.4);
  color: #fecaca;
}

.form-footer {
  text-align: center;
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.78);
}

.form-footer p {
  margin: 0;
}

.form-footer a {
  color: #ffffff;
  text-decoration: none;
  font-weight: 600;
}

.form-footer a:hover {
  text-decoration: underline;
}

/* OAuth */
.oauth-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 0.25rem;
}

.oauth-button {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.85rem 1.25rem;
  height: auto;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: transparent;
  color: #ffffff;
  font-weight: 500;
  font-size: 0.95rem;
  border-radius: 9999px;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.oauth-button:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.6);
}

.oauth-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.oauth-icon {
  flex-shrink: 0;
}

/* Divider */
.divider {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 0.75rem 0;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.75rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid rgba(255, 255, 255, 0.18);
}

.divider span {
  padding: 0 0.75rem;
}

/* Mobile */
@media (max-width: 480px) {
  .modal-content {
    padding: 2rem 1.5rem;
    border-radius: 16px;
  }

  .modal-header h2 {
    font-size: 1.4rem;
  }

  .modal-subtitle {
    font-size: 0.875rem;
  }
}
</style>

